import type {
  ElectricalDevice,
  ElectricalTerminal,
  SchematicSymbolRef,
} from './types';

/**
 * Endüstriyel Kumanda Cihazları Kataloğu ve Fabrika Fonksiyonları
 */

export function createPowerSource(id = 'pwr_source', x = 60, y = 80): ElectricalDevice {
  const terminals: ElectricalTerminal[] = [
    { id: 'L1', label: 'L1 (R)', role: 'supply_phase', panelPinOffset: { x: 14, y: 12 } },
    { id: 'L2', label: 'L2 (S)', role: 'supply_phase', panelPinOffset: { x: 38, y: 12 } },
    { id: 'L3', label: 'L3 (T)', role: 'supply_phase', panelPinOffset: { x: 62, y: 12 } },
    { id: 'N', label: 'N', role: 'supply_neutral', panelPinOffset: { x: 86, y: 12 } },
    { id: 'PE', label: 'PE', role: 'ground', panelPinOffset: { x: 110, y: 12 } },
  ];

  const schematicSymbols: SchematicSymbolRef[] = [
    {
      symbolId: `${id}_power_rails`,
      role: 'power_contacts',
      terminalMapping: { L1: 'L1', L2: 'L2', L3: 'L3', N: 'N', PE: 'PE' },
      x: 40,
      y: 40,
    },
  ];

  return {
    id,
    designator: 'PWR',
    type: 'power-source',
    name: '3 Faz 400V~ Şebeke Besleme Rayı',
    terminals,
    panelPlacement: { railId: 'top-busbar', x, y },
    schematicSymbols,
    props: { voltagePhasePhase: 400, voltagePhaseNeutral: 230, frequencyHz: 50 },
  };
}

export function createMcb3P(id = 'f1', designator = 'F1', x = 140, y = 200): ElectricalDevice {
  const terminals: ElectricalTerminal[] = [
    // Üst giriş klemensleri
    { id: '1', label: '1/L1', role: 'power_in', panelPinOffset: { x: 18, y: 14 } },
    { id: '3', label: '3/L2', role: 'power_in', panelPinOffset: { x: 44, y: 14 } },
    { id: '5', label: '5/L3', role: 'power_in', panelPinOffset: { x: 70, y: 14 } },
    // Alt çıkış klemensleri
    { id: '2', label: '2/T1', role: 'power_out', panelPinOffset: { x: 18, y: 116 } },
    { id: '4', label: '4/T2', role: 'power_out', panelPinOffset: { x: 44, y: 116 } },
    { id: '6', label: '6/T3', role: 'power_out', panelPinOffset: { x: 70, y: 116 } },
  ];

  const schematicSymbols: SchematicSymbolRef[] = [
    {
      symbolId: `${id}_power_mcb`,
      role: 'power_contacts',
      terminalMapping: { '1': '1', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6' },
      x: 100,
      y: 80,
    },
  ];

  return {
    id,
    designator,
    type: 'mcb-3p',
    name: '3 Kutuplu Otomat Sigorta (16A C Tipi)',
    terminals,
    panelPlacement: { railId: 'upper-din-rail', x, y },
    schematicSymbols,
    props: { ratedCurrentAmps: 16, curve: 'C', closed: true },
  };
}

export function createMcb1P(id = 'f2', designator = 'F2', x = 250, y = 200): ElectricalDevice {
  const terminals: ElectricalTerminal[] = [
    { id: '1', label: '1', role: 'power_in', panelPinOffset: { x: 16, y: 14 } },
    { id: '2', label: '2', role: 'power_out', panelPinOffset: { x: 16, y: 116 } },
  ];

  const schematicSymbols: SchematicSymbolRef[] = [
    {
      symbolId: `${id}_ctrl_mcb`,
      role: 'power_contacts',
      terminalMapping: { '1': '1', '2': '2' },
      x: 320,
      y: 80,
    },
  ];

  return {
    id,
    designator,
    type: 'mcb-1p',
    name: '1 Kutuplu Kumanda Sigortası (6A B Tipi)',
    terminals,
    panelPlacement: { railId: 'upper-din-rail', x, y },
    schematicSymbols,
    props: { ratedCurrentAmps: 6, curve: 'B', closed: true },
  };
}

export function createContactor3P(id = 'km1', designator = 'KM1', x = 330, y = 200): ElectricalDevice {
  const terminals: ElectricalTerminal[] = [
    // Bobin klemensleri
    { id: 'A1', label: 'A1', role: 'coil', panelPinOffset: { x: 20, y: 16 } },
    { id: 'A2', label: 'A2', role: 'coil', panelPinOffset: { x: 80, y: 124 } },
    // 3 Ana Güç Kontağı Girişleri
    { id: '1', label: '1/L1', role: 'power_in', panelPinOffset: { x: 32, y: 16 } },
    { id: '3', label: '3/L2', role: 'power_in', panelPinOffset: { x: 50, y: 16 } },
    { id: '5', label: '5/L3', role: 'power_in', panelPinOffset: { x: 68, y: 16 } },
    // 3 Ana Güç Kontağı Çıkışları
    { id: '2', label: '2/T1', role: 'power_out', panelPinOffset: { x: 32, y: 124 } },
    { id: '4', label: '4/T2', role: 'power_out', panelPinOffset: { x: 50, y: 124 } },
    { id: '6', label: '6/T3', role: 'power_out', panelPinOffset: { x: 68, y: 124 } },
    // Yardımcı Mühürleme Kontağı (NO 13-14)
    { id: '13', label: '13 NO', role: 'aux_no', panelPinOffset: { x: 86, y: 16 } },
    { id: '14', label: '14 NO', role: 'aux_no', panelPinOffset: { x: 86, y: 92 } },
    // Yardımcı Kapalı Kontak (NC 21-22)
    { id: '21', label: '21 NC', role: 'aux_nc', panelPinOffset: { x: 14, y: 36 } },
    { id: '22', label: '22 NC', role: 'aux_nc', panelPinOffset: { x: 14, y: 104 } },
  ];

  const schematicSymbols: SchematicSymbolRef[] = [
    {
      symbolId: `${id}_main_contacts`,
      role: 'power_contacts',
      terminalMapping: { '1': '1', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6' },
      x: 100,
      y: 180,
    },
    {
      symbolId: `${id}_coil`,
      role: 'coil',
      terminalMapping: { A1: 'A1', A2: 'A2' },
      x: 320,
      y: 380,
    },
    {
      symbolId: `${id}_aux_13_14`,
      role: 'aux_no',
      terminalMapping: { '13': '13', '14': '14' },
      x: 390,
      y: 280,
    },
  ];

  return {
    id,
    designator,
    type: 'contactor-3p',
    name: '3 Fazlı Güç Kontaktörü (230V AC Bobin)',
    terminals,
    panelPlacement: { railId: 'upper-din-rail', x, y },
    schematicSymbols,
    props: { coilVoltageAC: 230, pickupRatio: 0.85, dropRatio: 0.4 },
  };
}

export function createThermalRelay(id = 'fr1', designator = 'FR1', x = 330, y = 350): ElectricalDevice {
  const terminals: ElectricalTerminal[] = [
    // Ana akım yolu girişleri (genelde kontaktör çıkışına doğrudan takılır)
    { id: '1', label: '1/L1', role: 'power_in', panelPinOffset: { x: 32, y: 12 } },
    { id: '3', label: '3/L2', role: 'power_in', panelPinOffset: { x: 50, y: 12 } },
    { id: '5', label: '5/L3', role: 'power_in', panelPinOffset: { x: 68, y: 12 } },
    // Ana akım yolu çıkışları (motora gider)
    { id: '2', label: '2/T1', role: 'power_out', panelPinOffset: { x: 32, y: 92 } },
    { id: '4', label: '4/T2', role: 'power_out', panelPinOffset: { x: 50, y: 92 } },
    { id: '6', label: '6/T3', role: 'power_out', panelPinOffset: { x: 68, y: 92 } },
    // Kumanda devresi koruma kontağı (NC 95-96)
    { id: '95', label: '95 NC', role: 'trip_nc', panelPinOffset: { x: 14, y: 72 } },
    { id: '96', label: '96 NC', role: 'trip_nc', panelPinOffset: { x: 26, y: 72 } },
    // Arıza sinyal kontağı (NO 97-98)
    { id: '97', label: '97 NO', role: 'trip_no', panelPinOffset: { x: 74, y: 72 } },
    { id: '98', label: '98 NO', role: 'trip_no', panelPinOffset: { x: 86, y: 72 } },
  ];

  const schematicSymbols: SchematicSymbolRef[] = [
    {
      symbolId: `${id}_heaters`,
      role: 'thermal_contacts',
      terminalMapping: { '1': '1', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6' },
      x: 100,
      y: 280,
    },
    {
      symbolId: `${id}_contact_95_96`,
      role: 'aux_nc',
      terminalMapping: { '95': '95', '96': '96' },
      x: 320,
      y: 150,
    },
    {
      symbolId: `${id}_contact_97_98`,
      role: 'aux_no',
      terminalMapping: { '97': '97', '98': '98' },
      x: 460,
      y: 150,
    },
  ];

  return {
    id,
    designator,
    type: 'thermal-relay',
    name: '3 Kutuplu Termik Aşırı Akım Rölesi (4-6.3A)',
    terminals,
    panelPlacement: { railId: 'upper-din-rail', x, y },
    schematicSymbols,
    props: { currentSettingAmps: 5.0, tripped: false },
  };
}

export function createStopButton(id = 'sb1', designator = 'SB1', x = 480, y = 140): ElectricalDevice {
  const terminals: ElectricalTerminal[] = [
    { id: '1', label: '1 (NC)', role: 'aux_nc', panelPinOffset: { x: 16, y: 16 } },
    { id: '2', label: '2 (NC)', role: 'aux_nc', panelPinOffset: { x: 16, y: 44 } },
  ];

  const schematicSymbols: SchematicSymbolRef[] = [
    {
      symbolId: `${id}_nc_button`,
      role: 'button_contact',
      terminalMapping: { '1': '1', '2': '2' },
      x: 320,
      y: 210,
    },
  ];

  return {
    id,
    designator,
    type: 'pushbutton-nc',
    name: 'Stop Butonu (Kırmızı, 1-2 NC)',
    terminals,
    panelPlacement: { railId: 'door-controls', x, y },
    schematicSymbols,
    props: { pressed: false, color: '#ef4444' },
  };
}

export function createStartButton(id = 'sb2', designator = 'SB2', x = 480, y = 220): ElectricalDevice {
  const terminals: ElectricalTerminal[] = [
    { id: '3', label: '3 (NO)', role: 'aux_no', panelPinOffset: { x: 16, y: 16 } },
    { id: '4', label: '4 (NO)', role: 'aux_no', panelPinOffset: { x: 16, y: 44 } },
  ];

  const schematicSymbols: SchematicSymbolRef[] = [
    {
      symbolId: `${id}_no_button`,
      role: 'button_contact',
      terminalMapping: { '3': '3', '4': '4' },
      x: 320,
      y: 280,
    },
  ];

  return {
    id,
    designator,
    type: 'pushbutton-no',
    name: 'Start Butonu (Yeşil, 3-4 NO)',
    terminals,
    panelPlacement: { railId: 'door-controls', x, y },
    schematicSymbols,
    props: { pressed: false, color: '#22c55e' },
  };
}

export function createPilotLamp(id = 'hl1', designator = 'HL1', color = '#22c55e', x = 480, y = 300): ElectricalDevice {
  const terminals: ElectricalTerminal[] = [
    { id: 'X1', label: 'X1', role: 'lamp_pin', panelPinOffset: { x: 16, y: 14 } },
    { id: 'X2', label: 'X2', role: 'lamp_pin', panelPinOffset: { x: 16, y: 42 } },
  ];

  const schematicSymbols: SchematicSymbolRef[] = [
    {
      symbolId: `${id}_lamp`,
      role: 'lamp',
      terminalMapping: { X1: 'X1', X2: 'X2' },
      x: 390,
      y: 380,
    },
  ];

  return {
    id,
    designator,
    type: 'pilot-lamp',
    name: `Sinyal Lambası (${designator})`,
    terminals,
    panelPlacement: { railId: 'door-controls', x, y },
    schematicSymbols,
    props: { color, voltageAC: 230 },
  };
}

export function createMotor3Phase(id = 'm1', designator = 'M1', x = 240, y = 560): ElectricalDevice {
  const terminals: ElectricalTerminal[] = [
    { id: 'U1', label: 'U1', role: 'motor_phase', panelPinOffset: { x: 24, y: 20 } },
    { id: 'V1', label: 'V1', role: 'motor_phase', panelPinOffset: { x: 44, y: 20 } },
    { id: 'W1', label: 'W1', role: 'motor_phase', panelPinOffset: { x: 64, y: 20 } },
    { id: 'PE', label: 'PE', role: 'ground', panelPinOffset: { x: 84, y: 20 } },
  ];

  const schematicSymbols: SchematicSymbolRef[] = [
    {
      symbolId: `${id}_motor`,
      role: 'motor',
      terminalMapping: { U1: 'U1', V1: 'V1', W1: 'W1', PE: 'PE' },
      x: 100,
      y: 380,
    },
  ];

  return {
    id,
    designator,
    type: 'motor-3phase',
    name: '3 Fazlı Asenkron Motor (1.5 kW 400V~)',
    terminals,
    panelPlacement: { railId: 'external-motor', x, y },
    schematicSymbols,
    props: { powerKw: 1.5, ratedCurrentAmps: 3.4, rpm: 1440, running: false },
  };
}

export function createWireDuct(
  id: string,
  designator: string,
  name: string,
  orientation: 'horizontal' | 'vertical',
  x: number,
  y: number,
  width: number,
  height: number
): ElectricalDevice {
  return {
    id,
    designator,
    type: 'wire-duct',
    name,
    terminals: [],
    panelPlacement: { railId: 'cable-management', x, y },
    schematicSymbols: [],
    props: { orientation, width, height },
  };
}

export function createTopWireDuct(id = 'duct_top'): ElectricalDevice {
  return createWireDuct(id, 'W-TOP', 'Üst Yatay Kablo Kanalı (500x34 mm)', 'horizontal', 68, 145, 504, 34);
}

export function createMidWireDuct(id = 'duct_mid'): ElectricalDevice {
  return createWireDuct(id, 'W-MID', 'Orta Yatay Kablo Kanalı (500x34 mm)', 'horizontal', 68, 435, 504, 34);
}

export function createLeftWireDuct(id = 'duct_left'): ElectricalDevice {
  return createWireDuct(id, 'W-LEFT', 'Sol Dikey Kablo Kanalı (34x290 mm)', 'vertical', 48, 145, 34, 324);
}

export function createRightWireDuct(id = 'duct_right'): ElectricalDevice {
  return createWireDuct(id, 'W-RIGHT', 'Sağ Dikey Kablo Kanalı (34x290 mm)', 'vertical', 558, 145, 34, 324);
}

